import { formatNumber } from '@ecom/sdk';
import type { ProductSort, ProductStatus, ProductSummaryDto } from '@ecom/contracts';
import type { PillTone } from '@/components/ui';
import type { AdminLocale } from '@/i18n';

/**
 * The product list's rules (docs/07-Admin/Products.md): what its statuses look
 * like, the orders it offers, what a selection can be moved to, and where a
 * row's product lives on the shop.
 */

/** Each status's colour — the product page's, so a product reads the same on both screens. */
export const PRODUCT_STATUS_TONE: Record<ProductStatus, PillTone> = {
  DRAFT: 'neutral',
  ACTIVE: 'success',
  ARCHIVED: 'warning',
};

/**
 * The orders the list offers, the API's default first. `featured` is left
 * out: it is one category's hand-picked order, set and seen on Categories.
 */
export const PRODUCT_SORTS: ProductSort[] = ['newest', 'bestselling', 'rating', 'price_asc', 'price_desc', 'name_asc'];

/** What a selection can be moved to, in the order the bar offers them. */
export const BULK_PRODUCT_STATUSES: ProductStatus[] = ['ACTIVE', 'DRAFT', 'ARCHIVED'];

type Selected = Pick<ProductSummaryDto, 'id' | 'status'>;

/** The moves worth offering a selection: each one that would change at least one product in it. */
export function bulkProductMoves(products: readonly Selected[]): ProductStatus[] {
  return BULK_PRODUCT_STATUSES.filter((target) => products.some((product) => product.status !== target));
}

/** The products a move would change; one already there is left alone rather than written again. */
export function needingMove(products: readonly Selected[], target: ProductStatus): string[] {
  return products.filter((product) => product.status !== target).map((product) => product.id);
}

/**
 * Stars out of five from the API's hundredths (450 is 4.5), to one decimal —
 * or null when nobody has reviewed it, because no reviews is not a zero.
 */
export function averageStars(ratingAvg: number, ratingCount: number): number | null {
  if (ratingCount <= 0) return null;
  return Math.round(ratingAvg / 10) / 10;
}

/** A star average in the reader's digits, keeping its one decimal ("4.5", "٤٫٥"). */
export function starsLabel(stars: number, locale: AdminLocale): string {
  return formatNumber(stars, locale, { maximumFractionDigits: 1 });
}

/**
 * The product's page on the shop, or null when there is none to open: the
 * deployment has not said where the shop is, or the product is not on sale —
 * a draft's address is a "not found" page. The default language's address,
 * which the shop serves unprefixed (ADR-017).
 */
export function storefrontProductUrl(
  storefrontUrl: string | null | undefined,
  product: Pick<ProductSummaryDto, 'slug' | 'status'>,
): string | null {
  if (!storefrontUrl || product.status !== 'ACTIVE') return null;
  return `${storefrontUrl.replace(/\/+$/, '')}/products/${encodeURIComponent(product.slug)}`;
}
